import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import pkg from '../../../../package.json';

const oryPublicUrl = import.meta.env.VITE_ORY_KRATOS_PUBLIC_URL as string;

interface OryNode {
  group: string;
  attributes?: { name?: string; value?: string };
}

interface OryUiMessage {
  id?: number;
  text?: string;
  type?: string;
  context?: unknown;
}

interface OryFlow {
  ui?: { nodes?: OryNode[]; action?: string; messages?: OryUiMessage[] };
  oauth2_login_request?: {
    oidc_context?: { login_hint?: string };
  };
}

type SsoStep = 'initial_flow_fetch' | 'identifier_submit' | 'refetch_flow' | 'oidc_submit';

async function reportSsoBridgeFailure(step: SsoStep, flowId: string, response: Response) {
  const body = await response.clone().text().catch(() => '<unreadable>');
  datadogRum.addError(new Error(`SSO bridge failure at ${step}`), {
    Version: pkg.version,
    team: 'authentication',
    step,
    flowId,
    status: response.status,
    body: body.slice(0, 2000),
  });
}

function reportSsoBridgeFlowState(flowId: string, flow: OryFlow, stage: string) {
  const nodes = flow.ui?.nodes ?? [];
  datadogRum.addAction('sso_bridge_flow_state', {
    Version: pkg.version,
    flowId,
    stage,
    hasOidcProvider: nodes.some(n => n.group === 'oidc' && n.attributes?.name === 'provider'),
    hasIdentifierFirst: nodes.some(n => n.group === 'identifier_first' && n.attributes?.name === 'identifier'),
    hasLoginHint: !!flow.oauth2_login_request?.oidc_context?.login_hint,
    uiMessages: flow.ui?.messages,
  });
}

function findNode(nodes: OryNode[], group: string, name: string): OryNode | null {
  return nodes.find(n => n.group === group && n.attributes?.name === name) ?? null;
}

function csrfToken(nodes: OryNode[]): string {
  return findNode(nodes, 'default', 'csrf_token')?.attributes?.value ?? '';
}

export function useOrySsoBridge(flowId: string | null) {
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!flowId || !oryPublicUrl) {
      navigate('/login', { replace: true });
      return;
    }

    const run = async () => {
      try {
        let r = await fetch(
          `${oryPublicUrl}/self-service/login/flows?id=${encodeURIComponent(flowId)}`,
          { credentials: 'include', headers: { Accept: 'application/json' } },
        );
        if (!r.ok) {
          await reportSsoBridgeFailure('initial_flow_fetch', flowId, r);
          setIsError(true);
          return;
        }
        let flow: OryFlow = await r.json();
        let nodes = flow.ui?.nodes ?? [];
        let action = flow.ui?.action;
        reportSsoBridgeFlowState(flowId, flow, 'initial_flow_fetch');

        // Identifier-first step: Ory shows the email field before revealing the OIDC
        // provider. Auto-submit the login_hint from the OAuth2 request context.
        if (!findNode(nodes, 'oidc', 'provider') && findNode(nodes, 'identifier_first', 'identifier')) {
          const hint = flow.oauth2_login_request?.oidc_context?.login_hint;
          if (!hint || !action) {
            datadogRum.addError(new Error('SSO bridge missing login_hint or action'), {
              Version: pkg.version,
              team: 'authentication',
              step: 'identifier_submit',
              flowId,
              hasHint: !!hint,
              hasAction: !!action,
            });
            setIsError(true);
            return;
          }

          const identifierRes = await fetch(action, {
            method: 'POST',
            credentials: 'include',
            redirect: 'manual',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Accept: 'application/json',
            },
            body: new URLSearchParams({
              identifier: hint,
              method: 'identifier_first',
              csrf_token: csrfToken(nodes),
            }).toString(),
          });

          if (!identifierRes.ok && identifierRes.type !== 'opaqueredirect') {
            await reportSsoBridgeFailure('identifier_submit', flowId, identifierRes);
          }

          r = await fetch(
            `${oryPublicUrl}/self-service/login/flows?id=${encodeURIComponent(flowId)}`,
            { credentials: 'include', headers: { Accept: 'application/json' } },
          );
          if (!r.ok) {
            await reportSsoBridgeFailure('refetch_flow', flowId, r);
            setIsError(true);
            return;
          }
          flow = await r.json();
          nodes = flow.ui?.nodes ?? [];
          action = flow.ui?.action;
          reportSsoBridgeFlowState(flowId, flow, 'refetch_flow');
        }

        const providerNode = findNode(nodes, 'oidc', 'provider');
        const provider = providerNode?.attributes?.value;
        if (!provider || !action) {
          datadogRum.addError(new Error('SSO bridge missing OIDC provider or action'), {
            Version: pkg.version,
            team: 'authentication',
            step: 'oidc_submit',
            flowId,
            hasProvider: !!provider,
            hasAction: !!action,
            uiMessages: flow.ui?.messages,
          });
          setIsError(true);
          return;
        }

        // Submit the OIDC method. Ory responds with 422 and redirect_browser_to.
        const oidcRes = await fetch(action, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json',
          },
          body: new URLSearchParams({
            method: 'oidc',
            provider,
            csrf_token: csrfToken(nodes),
          }).toString(),
        });

        if (oidcRes.status === 422) {
          const body = await oidcRes.clone().json().catch(() => null);
          if (body?.redirect_browser_to) {
            window.location.replace(body.redirect_browser_to);
            return;
          }
          datadogRum.addError(new Error('SSO bridge 422 without redirect_browser_to'), {
            Version: pkg.version,
            team: 'authentication',
            step: 'oidc_submit',
            flowId,
            status: 422,
            body: JSON.stringify(body).slice(0, 2000),
          });
        } else {
          await reportSsoBridgeFailure('oidc_submit', flowId, oidcRes);
        }

        setIsError(true);
      } catch (err) {
        datadogRum.addError(err instanceof Error ? err : new Error(String(err)), {
          Version: pkg.version,
          team: 'authentication',
          step: 'exception',
          flowId,
        });
        setIsError(true);
      }
    };

    run();
  }, [flowId, navigate]);

  return { isError };
}
